/******/ (() => {
  // webpackBootstrap
  /* ===========================================================================
     <sticky-atc> — Mobile-only sticky Add-to-Cart for the PDP
     ---------------------------------------------------------------------------
     Mirrors the in-form `buy_buttons` block (the canonical ATC). On a mobile
     width the bar is shown as soon as it initialises and stays on screen at
     every scroll position, including while the canonical ATC is visible
     (AC-2014). On tap it delegates submission to the canonical
     <product-form> so that all line-item properties (DTA, Embroidery, Leash,
     Snap, GUA) are preserved. If the canonical ATC is disabled, it scrolls
     to the first option group with nothing chosen and focuses that group's
     first radio, so the shopper can finish their selection (AC-2014). With
     every option chosen and the ATC still disabled (sold out), it scrolls to
     the buy buttons instead.
     Issue #423 / tracker #2400.
  =========================================================================== */
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.canonicalForm = null;
      this.canonicalButton = null;
      this.canonicalText = null;
      this.observedTarget = null;
      this.mqMobile = null;
      this.isMobile = false;
      this.stickyButton = null;
      this.stickyText = null;
      this.stickyThumb = null;
      this.defaultThumbSrc = null;
      this.mutationObserver = null;
      this.handleClickBound = this.handleClick.bind(this);
      this.handleMqChangeBound = this.handleMqChange.bind(this);
      this.handleCartAddedBound = this.handleCartAdded.bind(this);
      this.handleCanonicalMutationBound = this.handleCanonicalMutation.bind(this);
      this.handleVariantUpdatedBound = this.handleVariantUpdated.bind(this);
      this.handleVariantClearedBound = this.handleVariantCleared.bind(this);
      this.handleMediaUpdatedBound = this.handleMediaUpdated.bind(this);
    }

    connectedCallback() {
      try {
        if (window.wetheme && window.wetheme.webcomponentRegistry) {
          // NB: key MUST match the `data-flow-load-key` used by the
          // <safe-load-scripts> wrapper that loads this file (see
          // sections/template--product.liquid). Otherwise SafeLoadScripts
          // will re-append the <script> on every Section Rendering API
          // re-render (variant change, cart update), bloating the DOM.
          window.wetheme.webcomponentRegistry.register({ key: "sticky-atc" });
        }
      } catch (e) {
        /* registry optional — non-fatal */
      }

      this.sectionId = this.getAttribute("data-section-id");
      this.productFormId = this.getAttribute("data-product-form-id");

      // Resolve DOM lazily — the canonical product-form is rendered by the
      // same section and is therefore guaranteed to exist by the time we
      // upgrade. Defer to the next frame to be safe with deferred scripts.
      requestAnimationFrame(() => this.init());
    }

    init() {
      // Guard against re-entry: appendChild(this) to body re-fires
      // connectedCallback, which would otherwise re-schedule init() and
      // double-attach observers / listeners.
      if (this._initDone) return;
      this._initDone = true;

      this.stickyButton = this.querySelector("[data-sticky-atc-button]");
      this.stickyText = this.querySelector("[data-sticky-atc-text]");
      this.stickyThumb = this.querySelector(".sticky-atc__thumb");
      // The base/featured image we restore whenever the selection isn't a
      // complete variant. Read it from data-default-src — NOT the initial src,
      // which on a ?variant= deep-link is already the variant's image, not the
      // base. Fall back to src for safety.
      this.defaultThumbSrc = this.stickyThumb
        ? this.stickyThumb.getAttribute("data-default-src") || this.stickyThumb.getAttribute("src")
        : null;
      if (!this.stickyButton) return;

      this.resolveCanonical();
      if (!this.canonicalForm || !this.canonicalButton) {
        // Canonical ATC not present (e.g. GUA + guest, or a fully-disabled
        // template). Keep the bar hidden and bail out — nothing to mirror.
        this.setHidden(true);
        return;
      }

      // Move to the end of <body> so we sit OUTSIDE the product section's
      // DOM subtree — 3rd-party app blocks can promote a section ancestor to
      // a containing block via transform/filter/contain, which would pin the
      // bar mid-page instead of the viewport bottom.
      if (this.parentNode !== document.body || this !== document.body.lastElementChild) {
        document.body.appendChild(this);
      }


      // The buy_buttons block container is where scrollToCanonical() takes
      // the shopper. Falls back to the canonical button itself if the wrapper
      // can't be located.
      this.observedTarget =
        this.canonicalForm.closest(".product-form-block") ||
        this.canonicalForm.parentElement ||
        this.canonicalButton;

      this.mqMobile = window.matchMedia("(max-width: 767px)");
      this.isMobile = this.mqMobile.matches;
      if (typeof this.mqMobile.addEventListener === "function") {
        this.mqMobile.addEventListener("change", this.handleMqChangeBound);
      } else if (typeof this.mqMobile.addListener === "function") {
        // Safari < 14
        this.mqMobile.addListener(this.handleMqChangeBound);
      }

      this.stickyButton.addEventListener("click", this.handleClickBound);

      // Mirror canonical state changes (text/disabled) through a
      // MutationObserver on the canonical button, so we react to
      // variant-driven changes too.
      this.mutationObserver = new MutationObserver(this.handleCanonicalMutationBound);
      this.mutationObserver.observe(this.canonicalButton, {
        attributes: true,
        attributeFilter: ["disabled", "class"],
        childList: true,
        subtree: true,
        characterData: true,
      });

      // The canonical [name="add"] button is rendered with `data-replace-content`
      // and replaceWith()-swapped on every variant change. When that happens,
      // the button MutationObserver above is now attached to a detached node
      // and stops delivering. Watch the parent <product-form> for childList
      // changes so we can re-resolve refs + re-attach + re-sync.
      this.formMutationObserver = new MutationObserver(() => {
        if (this.refreshCanonicalRefsIfStale()) {
          this.syncFromCanonical();
        }
      });
      this.formMutationObserver.observe(this.productFormEl, {
        childList: true,
        subtree: true,
      });

      // Re-sync on cart events emitted by component-product-form.js, and
      // mirror the gallery's image into the sticky thumbnail.
      if (window.eventBus && typeof window.eventBus.on === "function") {
        window.eventBus.on("cart:added", this.handleCartAddedBound);
        // Mirror the SAME events the media gallery listens to, so the thumb
        // and the gallery never disagree:
        //  - `variant:updated` fires only once a COMPLETE valid variant is
        //    resolved (component-product-information.js). It is NOT emitted for
        //    partial/invalid selections, so picking only Width never reaches it.
        //  - `variant:cleared` fires when a previously-complete selection is
        //    broken (e.g. Width changed, Design no longer resolves) → reset to
        //    the base image, matching the gallery's media:reset rebuild.
        window.eventBus.on("variant:updated", this.handleVariantUpdatedBound);
        window.eventBus.on("variant:cleared", this.handleVariantClearedBound);
        // Fires when the media wrapper was swapped (showOnlyVariantMedia /
        // combined listing) — the >250-variant / Original Designs path.
        window.eventBus.on("product:media:updated", this.handleMediaUpdatedBound);
      }

      // First mirror; updateVisibility() inside it shows the bar on mobile.
      this.syncFromCanonical();
    }

    resolveCanonical() {
      // Strategy: locate the <product-form> custom element rendered by the
      // payment-buttons snippet for this section. There is exactly one per
      // section. We prefer scoping by data-section-id, but fall back to a
      // global lookup so the gift-card / preorder / return templates still
      // wire up correctly.
      const sectionRoot = this.sectionId
        ? document.querySelector(`[data-wetheme-section-id="${this.sectionId}"]`)
        : null;

      let productForm = null;
      if (sectionRoot) {
        productForm = sectionRoot.querySelector("product-form");
      }
      if (!productForm) {
        productForm = document.querySelector("product-form");
      }
      if (!productForm) return;

      this.productFormEl = productForm;
      this.canonicalForm = productForm.querySelector("form");
      this.canonicalButton = productForm.querySelector("[name='add']");
      this.canonicalText = productForm.querySelector("[data-add-to-cart-text]");
    }

    refreshCanonicalRefsIfStale() {
      // The canonical submit button is rendered with data-replace-content and
      // gets replaceWith()-swapped by product-information.replaceElements()
      // on every variant change. After that, our stored canonicalButton (and
      // the MutationObserver attached to it) point at a detached node. Detect
      // that and re-bind in place so syncFromCanonical / handleClick keep
      // working.
      if (!this.productFormEl || !this.productFormEl.isConnected) return false;
      const liveButton = this.productFormEl.querySelector("[name='add']");
      if (liveButton && liveButton !== this.canonicalButton) {
        this.canonicalButton = liveButton;
        this.canonicalText = this.productFormEl.querySelector("[data-add-to-cart-text]");
        if (this.mutationObserver) {
          try {
            this.mutationObserver.disconnect();
          } catch (e) {}
          this.mutationObserver.observe(this.canonicalButton, {
            attributes: true,
            attributeFilter: ["disabled", "class"],
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
        return true;
      }
      return false;
    }

    handleMqChange(e) {
      this.isMobile = e.matches;
      this.updateVisibility();
    }

    handleCanonicalMutation() {
      this.syncFromCanonical();
    }

    handleCartAdded() {
      // After a successful add, the canonical button briefly says "Added";
      // re-sync so the sticky bar reflects that and any disabled state.
      this.syncFromCanonical();
    }

    handleVariantUpdated(payload) {
      // A complete, valid variant was resolved. The variant object on this
      // event is NOT reliable as an image source: for products with
      // `showOnlyVariantMedia` and/or >250 variants the theme resolves media by
      // re-rendering the gallery server-side, so the payload variant is a thin
      // { id, available } with no featured_image. The media gallery DOM, on the
      // other hand, has already been updated by the time this fires
      // (product-information.replaceElements runs BEFORE the emit). So we mirror
      // whatever the gallery is now showing — the single source of truth.
      if (!payload || !this.stickyThumb) return;
      if (payload.sectionId != this.sectionId) return;

      // "Not seen here" design (no media of its own) → gallery resets to the
      // product default; mirror that without scraping.
      if (payload.resetMediaToDefault) {
        this.setThumbSrc(this.defaultThumbSrc);
        return;
      }

      this.syncThumbFromGallery();
    }

    handleVariantCleared(payload) {
      // A previously-complete selection was broken (e.g. only Width remains
      // selected, or the shopper deselected). The gallery rebuilds to the
      // default media via `media:reset`; mirror that on the thumbnail.
      if (!payload || !this.stickyThumb) return;
      if (payload.sectionId != this.sectionId) return;
      this.setThumbSrc(this.defaultThumbSrc);
    }

    handleMediaUpdated(payload) {
      // Emitted by component-product-media.js when the media wrapper itself was
      // swapped (showOnlyVariantMedia / combined listing). Re-read the gallery.
      if (!payload || !this.stickyThumb) return;
      if (payload.sectionId != this.sectionId) return;
      this.syncThumbFromGallery();
    }

    syncThumbFromGallery() {
      if (!this.stickyThumb) return;
      // Defer one frame so Swiper has applied the new active slide / finished a
      // re-init after a wrapper swap before we read.
      const raf =
        window.requestAnimationFrame || function (cb) { return window.setTimeout(cb, 16); };
      raf(() => {
        const src = this.readGalleryMainImageSrc();
        this.setThumbSrc(this.resizeImageUrl(src, 160) || this.defaultThumbSrc);
      });
    }

    readGalleryMainImageSrc() {
      // Read the image the main product gallery is currently showing for this
      // section. Prefer the active slide (correct for the shared-media slideTo
      // path); fall back to the first real slide (correct for showOnlyVariantMedia,
      // where the re-rendered wrapper's first slide IS the variant media and the
      // active class may not be set yet mid re-init). Skip Swiper's looped
      // duplicate slides.
      const gallery = this.sectionId
        ? document.querySelector(`product-media[data-section-id="${this.sectionId}"]`)
        : document.querySelector("product-media");
      const main = gallery && gallery.querySelector("[data-slider-main]");
      if (!main) return null;
      const img =
        main.querySelector(".swiper-slide-active:not(.swiper-slide-duplicate) img.theme-img") ||
        main.querySelector(".swiper-slide:not(.swiper-slide-duplicate) img.theme-img") ||
        main.querySelector("img.theme-img");
      return img ? img.getAttribute("src") : null;
    }

    setThumbSrc(src) {
      if (!src || !this.stickyThumb) return;
      if (this.stickyThumb.getAttribute("src") !== src) {
        this.stickyThumb.setAttribute("src", src);
      }
    }

    resizeImageUrl(src, width) {
      // Append/override the Shopify CDN `width` param so we don't ship a
      // full-resolution image into an 80px thumb. Handles an existing `?v=...`
      // query and protocol-relative (`//cdn...`) URLs.
      if (!src) return null;
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set("width", String(width));
        return url.href;
      } catch (e) {
        return src;
      }
    }

    syncFromCanonical() {
      if (this.canonicalButton && !this.canonicalButton.isConnected) {
        this.refreshCanonicalRefsIfStale();
      }
      if (!this.canonicalButton || !this.stickyButton) return;

      // Mirror disabled state via aria-disabled ONLY — never set the native
      // `disabled` property on the sticky button. A native-disabled button
      // does not fire click events, which would kill our scroll-to-canonical
      // fallback (AC: "Tap without variant → smooth scroll to canonical ATC
      // + focus"). We keep the button focusable/clickable and decide what to
      // do inside handleClick().
      const disabled = !!this.canonicalButton.disabled;
      if (disabled) {
        this.stickyButton.setAttribute("aria-disabled", "true");
      } else {
        this.stickyButton.removeAttribute("aria-disabled");
      }

      // Mirror text. Prefer the visible add-to-cart label; fall back to the
      // button textContent if the label span is hidden (e.g. mid-loading).
      if (this.stickyText) {
        const sourceText = this.canonicalText && this.canonicalText.textContent
          ? this.canonicalText.textContent.trim()
          : (this.canonicalButton.textContent || "").trim();
        if (sourceText) {
          this.stickyText.textContent = sourceText;
        }
      }

      // Loading state pass-through (canonical sets a spinner via class
      // mutations on [data-loading-icon] inside the button).
      const loadingIcon = this.canonicalButton.querySelector("[data-loading-icon]");
      const isLoading = !!(loadingIcon && !loadingIcon.classList.contains("hidden"));
      this.stickyButton.classList.toggle("is-loading", isLoading);

      this.updateVisibility();
    }

    updateVisibility() {
      // The bar is GLOBALLY hidden on desktop via CSS. The aria/inert state
      // still needs to be authoritative on mobile so AT and tab order are
      // correct. On mobile the bar is shown at every scroll position.
      const shouldShow = this.isMobile && !!this.canonicalButton;
      this.setHidden(!shouldShow);
    }

    setHidden(hidden) {
      if (hidden) {
        this.setAttribute("aria-hidden", "true");
        this.classList.remove("is-visible");
        if (this.stickyButton) {
          this.stickyButton.setAttribute("tabindex", "-1");
        }
      } else {
        this.removeAttribute("aria-hidden");
        this.classList.add("is-visible");
        if (this.stickyButton) {
          this.stickyButton.removeAttribute("tabindex");
        }
      }
    }

    handleClick(event) {
      event.preventDefault();
      if (this.canonicalButton && !this.canonicalButton.isConnected) {
        this.refreshCanonicalRefsIfStale();
      }
      if (!this.canonicalForm || !this.canonicalButton) return;

      // Hybrid behavior:
      //  - canonical disabled  → scroll to the first unchosen option group
      //                          and focus its first radio; if every group
      //                          is chosen (sold out), scroll to the buy
      //                          buttons. See scrollToCanonical.
      //  - canonical enabled   → dispatch submit on the canonical form so
      //                          every hidden input / line-item property the
      //                          PDP apps wrote stays intact.
      //
      // NB: we read `disabled` from the canonical button directly (not from
      // our own button) because the sticky button never gets the native
      // `disabled` attribute — only `aria-disabled`. See syncFromCanonical.
      if (this.canonicalButton.disabled) {
        this.scrollToCanonical();
        return;
      }

      // Use requestSubmit so the canonical form's `submit` listener (set by
      // <product-form>) fires exactly as if the user clicked the in-form ATC.
      try {
        if (typeof this.canonicalForm.requestSubmit === "function") {
          this.canonicalForm.requestSubmit(this.canonicalButton);
        } else {
          // Legacy fallback — synthesise a submit event the same way native
          // form submission would.
          const submitEvent = new Event("submit", { bubbles: true, cancelable: true });
          this.canonicalForm.dispatchEvent(submitEvent);
        }
      } catch (e) {
        console.error("[sticky-atc] failed to delegate submit:", e);
      }
    }

    scrollToCanonical() {
      const group = this.findFirstUnchosenOptionGroup();
      if (group) {
        try {
          group.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (e) {
          group.scrollIntoView();
        }

        // Defer focus so it lands after the smooth scroll has started. This is
        // a programmatic response to the user activating the sticky button,
        // therefore NOT a 3.2.2 "on input" violation. Focus alone checks no
        // radio: product-swatch reacts to `change` only. Re-query, because an
        // auto-select re-render can replace the options block meanwhile; a
        // radio from the old block is detached and is skipped.
        window.setTimeout(() => {
          const radio = this.firstFocusableRadio(group);
          if (radio && radio.isConnected) {
            radio.focus({ preventScroll: true });
          }
        }, 350);
        return;
      }

      // Every option is chosen and the ATC is still disabled (sold out): show
      // the buy buttons. No focus call: a disabled button ignores focus().
      const target =
        this.observedTarget ||
        this.canonicalButton.closest(".product-form-block") ||
        this.canonicalButton;
      if (!target) return;

      try {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (e) {
        target.scrollIntoView();
      }
    }

    findFirstUnchosenOptionGroup() {
      // Option groups render in option order (Width, Designs, Size), so the
      // first visible one with radios and none checked is what the shopper
      // has to answer next.
      const sectionRoot = this.sectionId
        ? document.querySelector(`[data-wetheme-section-id="${this.sectionId}"]`)
        : null;
      const groups = (sectionRoot || document).querySelectorAll("[data-product-options] product-swatch");
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        if (!group.getClientRects().length) continue;
        if (!group.querySelector('input[type="radio"]')) continue;
        if (group.querySelector('input[type="radio"]:checked')) continue;
        return group;
      }
      return null;
    }

    firstFocusableRadio(group) {
      // A value filtered out by product-information (or a collapsed DTA row)
      // sits in a display:none tile, and focus() on its radio does nothing.
      const radios = group.querySelectorAll('input[type="radio"]');
      for (let i = 0; i < radios.length; i++) {
        const tile = radios[i].closest(".swatch-element") || radios[i];
        if (!radios[i].disabled && tile.getClientRects().length) return radios[i];
      }
      return radios[0] || null;
    }

    disconnectedCallback() {
      if (this.mutationObserver) {
        try {
          this.mutationObserver.disconnect();
        } catch (e) {}
        this.mutationObserver = null;
      }
      if (this.formMutationObserver) {
        try {
          this.formMutationObserver.disconnect();
        } catch (e) {}
        this.formMutationObserver = null;
      }
      if (this.mqMobile) {
        if (typeof this.mqMobile.removeEventListener === "function") {
          this.mqMobile.removeEventListener("change", this.handleMqChangeBound);
        } else if (typeof this.mqMobile.removeListener === "function") {
          this.mqMobile.removeListener(this.handleMqChangeBound);
        }
      }
      if (this.stickyButton) {
        this.stickyButton.removeEventListener("click", this.handleClickBound);
      }
      if (window.eventBus && typeof window.eventBus.off === "function") {        
        window.eventBus.off("cart:added", this.handleCartAddedBound);
        window.eventBus.off("variant:updated", this.handleVariantUpdatedBound);
        window.eventBus.off("variant:cleared", this.handleVariantClearedBound);
        window.eventBus.off("product:media:updated", this.handleMediaUpdatedBound);
      }
    }
  }

  if (!customElements.get("sticky-atc")) {
    customElements.define("sticky-atc", StickyAtc);
  }

  /******/
})();
